import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { api } from '../services/api';
import type { CreateRecurringServiceRequest } from '../types';

function formatMoney(amount: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
  }).format(amount);
}

export function ServicesPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<CreateRecurringServiceRequest>({
    name: '',
    amount: 0,
    due_day: 1,
    category: '',
  });

  const { data: services, isLoading } = useQuery({
    queryKey: ['services'],
    queryFn: () => api.getRecurringServices(),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateRecurringServiceRequest) => api.createRecurringService(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setShowForm(false);
      setFormData({ name: '', amount: 0, due_day: 1, category: '' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name && formData.amount > 0) {
      createMutation.mutate(formData);
    }
  };

  // Calculate summaries
  const activeServices = services?.filter((s) => s.is_active) || [];
  const totalMonthly = activeServices.reduce((sum, s) => sum + s.amount, 0);

  const today = new Date().getDate();
  const dueSoon = activeServices.filter((s) => {
    const daysUntilDue = s.due_day - today;
    return daysUntilDue >= 0 && daysUntilDue <= 7;
  });

  const categories = [
    { value: 'utilities', label: 'Servicios', icon: '💡' },
    { value: 'subscriptions', label: 'Suscripciones', icon: '📺' },
    { value: 'insurance', label: 'Seguros', icon: '🛡️' },
    { value: 'rent', label: 'Renta', icon: '🏠' },
    { value: 'loans', label: 'Créditos', icon: '🏦' },
    { value: 'other', label: 'Otros', icon: '📦' },
  ];

  const getCategoryInfo = (cat: string) => {
    return categories.find((c) => c.value === cat) || { label: cat, icon: '📦' };
  };

  return (
    <div>
      <Header title="Servicios Recurrentes" />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="stat-card">
          <div className="text-2xl mb-2">📋</div>
          <div className="stat-value">{activeServices.length}</div>
          <div className="stat-label">Servicios activos</div>
        </div>
        <div className="stat-card">
          <div className="text-2xl mb-2">💵</div>
          <div className="stat-value">{formatMoney(totalMonthly)}</div>
          <div className="stat-label">Total mensual</div>
        </div>
        <div className="stat-card">
          <div className="text-2xl mb-2">⏰</div>
          <div className="text-3xl font-bold text-yellow-400">{dueSoon.length}</div>
          <div className="stat-label">Próximos a vencer</div>
        </div>
      </div>

      {/* Add Button / Form */}
      <div className="card mb-6">
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary"
          >
            ➕ Nuevo Servicio
          </button>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <h2 className="text-lg font-semibold text-primary-500">Nuevo Servicio Recurrente</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Nombre del servicio</label>
                <input
                  type="text"
                  className="input"
                  placeholder="Ej: Luz CFE"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="label">Monto mensual</label>
                <input
                  type="number"
                  className="input"
                  step="0.01"
                  value={formData.amount || ''}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
              <div>
                <label className="label">Día de vencimiento</label>
                <input
                  type="number"
                  className="input"
                  min="1"
                  max="31"
                  value={formData.due_day}
                  onChange={(e) => setFormData({ ...formData, due_day: parseInt(e.target.value) || 1 })}
                  required
                />
              </div>
              <div>
                <label className="label">Categoría</label>
                <select
                  className="input"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <option value="">Seleccionar...</option>
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Guardando...' : 'Agregar Servicio'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Services List */}
      <div className="card">
        <h2 className="text-lg font-semibold text-primary-500 mb-4">📋 Mis Servicios</h2>
        {isLoading ? (
          <p className="text-gray-500 text-center py-4">Cargando...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services?.map((service) => {
              const catInfo = getCategoryInfo(service.category || 'other');
              const daysUntilDue = service.due_day - today;
              const isDueSoon = daysUntilDue >= 0 && daysUntilDue <= 7;
              const isOverdue = daysUntilDue < 0;

              return (
                <div
                  key={service.id}
                  className={`p-4 bg-dark-bg rounded-lg border-l-4 ${
                    isOverdue
                      ? 'border-red-500'
                      : isDueSoon
                      ? 'border-yellow-500'
                      : 'border-primary-500'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{catInfo.icon}</span>
                      <span className="font-semibold">{service.name}</span>
                    </div>
                    {!service.is_active && (
                      <span className="badge badge-danger">Inactivo</span>
                    )}
                  </div>
                  <div className="text-2xl font-bold text-primary-500 mb-2">
                    {formatMoney(service.amount)}
                  </div>
                  <div className="text-sm text-gray-500">
                    Vence el día {service.due_day} de cada mes
                  </div>
                  {isDueSoon && (
                    <div className="mt-2 text-xs text-yellow-400">
                      ⏰ Vence en {daysUntilDue} días
                    </div>
                  )}
                  {isOverdue && (
                    <div className="mt-2 text-xs text-red-400">
                      ⚠️ Vencido hace {Math.abs(daysUntilDue)} días
                    </div>
                  )}
                </div>
              );
            })}
            {!services?.length && (
              <p className="text-gray-500 text-center py-4 col-span-full">
                No hay servicios registrados
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
